﻿import { useEffect, useState } from 'react';
import confetti from 'canvas-confetti';
import useGameStore, { botAuctionDecision, botEvaluatePurchase, evaluateBotUpgrade, floatSharesOf, ownershipPct, pricePerShareOf, valuationOf } from './store/gameStore';
import { money } from './lib/format';
import GameBoard from './components/GameBoard';
import LobbyScreen from './components/LobbyScreen';
import HandoverScreen from './components/HandoverScreen';
import { HotseatTradeReview, TradeModal } from './components/TradeModal';
import ActionSteps from './components/ActionSteps';
import BotStatusBar from './components/BotStatusBar';
import DistrictPortfolio from './components/CapTable';
import FinancialDeskModal from './components/FinancialDeskModal';
import InsolvencyModal from './components/InsolvencyModal';
import NewPropertyAlertModal from './components/NewPropertyAlertModal';
import TileGuideModal from './components/TileGuideModal';
import { AuctionPanel } from './components/AuctionCards';
import ActivityTicker from './components/ActivityTicker';
import CloudSavePrompt from './components/CloudSavePrompt';
import SaveManagerModal from './components/SaveManagerModal';
import PlayerDossierModal from './components/PlayerDossierModal';
import MobileNav from './components/MobileNav';
import LangSwitcher from './components/LangSwitcher';
import { CompanySummary } from './components/BoardTable';
import useCloudStore from './store/cloudStore';
import { useStakkoOptional } from './stakko';
import { tileName, useT } from './i18n';
import { HeatmapIcon, MenuIcon, SaveIcon, SpeedIcon, TradeIcon } from './components/GameIcons';
import './index.css';

const App = () => {
  const store = useGameStore();
  const t = useT();
  const stakko = useStakkoOptional();
  const stakkoUid = stakko?.user?.uid ?? null;
  const {
    board,
    players,
    market,
    currentPlayerIndex,
    turnPhase,
    pushLuckUsed,
    reachableIndices,
    auction,
    proposal,
    pendingOffer,
    emergency,
    insolvency,
    attention,
    tradeOffer,
    gameOver,
    epoch,
    gameStarted,
    handover,
    hotseatReview,
    moneyTransfers,
  } = store;

  const [heatmap, setHeatmap] = useState(false);
  const [tradeOpen, setTradeOpen] = useState(false);
  /** Landing screen on boot: the player can jump straight back into a running match. */
  const [showIntro, setShowIntro] = useState(true);
  const [speed, setSpeed] = useState<'NORMAL' | 'FAST'>(() => (localStorage.getItem('etycoon-speed') === 'FAST' ? 'FAST' : 'NORMAL'));
  const toggleSpeed = () => {
    setSpeed((s) => {
      const next = s === 'NORMAL' ? 'FAST' : 'NORMAL';
      localStorage.setItem('etycoon-speed', next);
      return next;
    });
  };

  // Dedicated Mobile Viewport (screens < 1024px / below lg): the desktop sidebar is
  // completely hidden and replaced by a thumb-friendly bottom nav + bottom sheets.
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 1023px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1023px)');
    const onChange = () => setIsMobile(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const [saveManagerOpen, setSaveManagerOpen] = useState(false);
  /** Active mobile bottom sheet (portfolio / rivals). */
  const [mobileSheet, setMobileSheet] = useState<'portfolio' | 'rivals' | null>(null);

  const active = players[currentPlayerIndex];
  // Hotseat: the "operator" at the controls is the active human during their turn.
  const human = active && !active.isAI ? active : players[0];
  const isHumanTurn = Boolean(active && !active.isAI);

  useEffect(() => {
    if (showIntro || !gameStarted || gameOver || !active?.isAI || auction.isOpen) return;
    // NOTE: pendingOffer is deliberately NOT a gate — the bot loop below resolves
    // its own unowned-property offer (buy or decline→auction). Gating on it here
    // desynced the bot and froze the game forever.
    if (emergency || insolvency || attention || tradeOffer || handover || hotseatReview) return; // gates freeze the loop
    if (turnPhase === 'TRADE_NEGOTIATION') return; // human is building a deal — pause the engine

    // Play-by-play broadcast (store-owned schema: bot turn phases, phase-tagged).
    if (turnPhase === 'TURN_START') store.setBotStatus({ playerId: active.id, phase: 'ROLL', text: `${active.name} rolling movement…` });
    if (turnPhase === 'MOVEMENT_PREVIEW' || turnPhase === 'PUSH_LUCK_DECISION') store.setBotStatus({ playerId: active.id, phase: 'MOVE', text: `${active.name} is choosing a stop…` });
    if (turnPhase === 'TILE_RESOLUTION') store.setBotStatus({ playerId: active.id, phase: 'SETTLE', text: `${active.name} is settling the tile…` });
    if (turnPhase === 'TURN_END' || turnPhase === 'GOVERNANCE_PHASE') store.setBotStatus({ playerId: active.id, phase: 'GOVERN', text: `${active.name} is at the governance desk…` });

    // Money-transfer pacing: when cash just moved, hold the step long enough for the
    // flying-cash chip (1.35 s) to land before the next bot action scrolls past.
    const flying = useGameStore.getState().moneyTransfers.some((t) => Date.now() - t.ts < 1200);
    const botStepDelay = flying ? 1300 : speed === 'FAST' ? 250 : 700;

    const timer = setTimeout(() => {
      const st = useGameStore.getState();
      // Bot resolves the unowned-property offer with classic Monopoly accumulation
      // behaviour: buy 80–90% of the time when affordable; auction ONLY when the bot
      // cannot afford the tranche but still wants to gamble on a cheap win; pass
      // ONLY when effectively broke (cash < $100).
      if (st.pendingOffer) {
        const o = st.pendingOffer;
        const tile = st.board.find((t) => t.id === o.tileId);
        if (tile && st.players[st.currentPlayerIndex]?.id === active.id) {
          if (active.cash < 100) {
            // Broke: pass entirely — shares return to the public float.
            st.setBotStatus({ playerId: active.id, phase: 'SETTLE', text: t('ticker.botBroke', { name: active.name, tile: tileName(tile.name, t) }) });
            st.clearOffer();
            st.finishTurn();
            return;
          }
          // Dynamic share-sizing: each archetype computes its own target tranche
          // (100 % buyout / 51 % / blocking 26–35 / safe 25–40 …) — never a fixed 25 cap.
          const decision = botEvaluatePurchase(st.board, active, tile);
          if (decision.shares > 0) {
            const cost = st.botExecutePurchase(active.id, o.tileId, decision.shares, decision.structure, decision.note);
            st.setBotStatus({
              playerId: active.id,
              phase: 'SETTLE',
              text: `${t('ticker.botBought', { name: active.name, shares: decision.shares, tile: tileName(tile.name, t), cost: money(cost) })} (${decision.targetPct.toFixed(0)} %)`,
            });
            st.clearOffer();
            return;
          }
          if (decision.wantAuction) {
            const gamble = (active.aiArchetype ?? 'OPPORTUNISTIC') === 'AGGRESSIVE' ? 0.7 : 0.5;
            if (Math.random() < gamble) {
              st.setBotStatus({
                playerId: active.id,
                phase: 'SETTLE',
                text: t('ticker.botAuction', { name: active.name, tile: tileName(tile.name, t), why: decision.note }),
              });
              st.clearOffer();
              st.stageAuction(o.tileId, Math.max(0, floatSharesOf(tile)), active.name);
              return;
            }
          }
          // Deliberate pass (has cash but chose not to buy): back to the float, keep playing.
          st.setBotStatus({
            playerId: active.id,
            phase: 'SETTLE',
            text: t('ticker.botPasses', { name: active.name, tile: tileName(tile.name, t), why: decision.note }),
          });
          st.clearOffer();
          return;
        }
      }
      if (turnPhase === 'TURN_START') {
        // Full Financial Desk pass once per round, before the dice leave the hand:
        // rent insurance, growth leverage and treasury parking. The store guards it
        // so a bot that already visited the desk this round is never double-charged.
        store.botFinancePhase(active.id);
        store.rollMovementPoints(6);
        return;
      }

      if (turnPhase === 'MOVEMENT_PREVIEW') {
        if (reachableIndices.length === 0) {
          store.rollMovementPoints(6);
          return;
        }
        const scored = reachableIndices
          .map((index) => {
            const tile = board[index];
            const preview = store.previewTileImpact(index);
            const owned = tile.shares[active.id] ?? 0;
            const float = tile.totalShares - Object.values(tile.shares).reduce((sum, value) => sum + value, 0);
            let score = preview.impact;
            if (tile.tileType === 'COMMERCIAL' && float > 0) score += 400 * (tile.upgradeLevel + 1);
            if (active.aiArchetype === 'CONSERVATIVE') score = preview.impact * 1.6 - preview.distance * 20;
            if (active.aiArchetype === 'OPPORTUNISTIC') score += owned > 0 ? 250 : 0;
            if (active.aiArchetype === 'AGGRESSIVE') score += tile.baseCost * 0.25;
            return { index, score };
          })
          .sort((a, b) => b.score - a.score);
        store.selectStopTile(scored[0].index);
        return;
      }

      if (turnPhase === 'PUSH_LUCK_DECISION') {
        const gambler = active.aiArchetype === 'AGGRESSIVE' && Math.random() < 0.45;
        const hoarder = active.aiArchetype === 'OPPORTUNISTIC' && Math.random() < 0.2;
        if (!pushLuckUsed && (gambler || hoarder)) store.pushYourLuck();
        else store.confirmStop();
        return;
      }

      if (turnPhase === 'GOVERNANCE_PHASE') {
        // Full Financial Desk routine once per round: treasury parking, growth leverage
        // and rent insurance. Guarded inside the store so the effect can re-run safely.
        store.botFinancePhase(active.id);

        // Financial & special tiles for bots: resolve passively and advance — never
        // open human-facing corporate action sheets. Passive benefit (emergency
        // liquidity via borrowing) is collected, then straight to TURN_END → nextTurn().
        const hereTile = board[active.currentTileIndex];
        if (hereTile && (hereTile.tileType === 'FINANCIAL' || hereTile.tileType === 'SPECIAL')) {
          if (active.cash < 800) store.borrow(active.id, 1200);
          store.setBotStatus({ playerId: active.id, phase: 'GOVERN', text: `${active.name} settled ${hereTile.name} — desk resolved` });
          store.finishTurn();
          return;
        }
        const archetype = active.aiArchetype ?? 'OPPORTUNISTIC';

        // Vulture Raider — contrarian market behaviour: hoard cash in BOOM,
        // hunt distressed bargains in CRISIS when valuations are depressed.
        if (archetype === 'OPPORTUNISTIC' && market.phase === 'CRISIS') {
          const distressed = board
            .filter((tile) => tile.tileType === 'COMMERCIAL' && floatSharesOf(tile) >= 10)
            .sort((a, b) => valuationOf(a, market.phase) / a.totalShares - valuationOf(b, market.phase) / b.totalShares)[0];
          if (distressed && active.cash > 400) {
            const bite = Math.min(floatSharesOf(distressed), Math.floor(active.cash / Math.max(1, pricePerShareOf(distressed))));
            if (bite >= 10) {
              const cost = store.botExecutePurchase(active.id, distressed.id, bite, 'DISTRESSED');
              store.setBotStatus({
                playerId: active.id,
                phase: 'GOVERN',
                text: t('ticker.botBought', { name: active.name, shares: bite, tile: tileName(distressed.name, t), cost: money(cost) }),
              });
              return;
            }
          }
        }
        // Prudence Banker — defensive reserve refill before anything else.
        if (archetype === 'CONSERVATIVE' && active.cash < 1200) {
          store.borrow(active.id, 1200);
        } else if (active.cash < 800) {
          store.borrow(active.id, 1200);
        }

        // Take-profit: minority stakes sold on >25% appreciation lock cash for auctions
        // (Aggressive/Opportunistic only — Prudence Banker holds for the long run).
        if (archetype !== 'CONSERVATIVE' && active.cash < 500) {
          const heldTiles = board
            .map((t) => ({ tile: t, owned: t.shares[active.id] ?? 0 }))
            .filter((h) => h.owned > 0 && h.owned < h.tile.totalShares / 2);
          const appreciated = heldTiles.filter((h) => {
            const gain = (h.tile.valuation - h.tile.baseCost) / Math.max(1, h.tile.baseCost);
            return gain > 0.25;
          });
          if (appreciated.length > 0 && Math.random() < 0.7) {
            const target = appreciated.sort((a, b) => b.tile.valuation - a.tile.valuation)[0];
            const gain = (target.tile.valuation - target.tile.baseCost) / Math.max(1, target.tile.baseCost);
            const sellPct = archetype === 'AGGRESSIVE' ? 0.3 : 0.2;
            const sharesToSell = Math.ceil(target.owned * sellPct);
            if (sharesToSell > 0) {
              store.sellShares(active.id, target.tile.id, sharesToSell);
              store.logEvent(`${active.name} took profit: sold ${sharesToSell} shares of ${target.tile.name} (${(gain * 100).toFixed(1)}% gain).`, 'GAIN');
              return;
            }
          }
        }

        // Upgrade doctrine per archetype (ARCHETYPE_PROFILES.upgrade — single source):
        // • AGGRESSIVE   upgrades a majority stake with a $100 reserve.
        // • CONSERVATIVE only with 3× the build cost still in reserve.
        // • OPPORTUNISTIC prioritises high-traffic positions with a $1,000 cushion.
        const intent = evaluateBotUpgrade(board, active);
        if (intent) {
          store.setBotStatus({
            playerId: active.id,
            phase: 'GOVERN',
            text: `${active.name} proposes upgrading ${intent.tileName} to T${intent.targetLevel} (${intent.reason})`,
          });
          store.proposeUpgrade(intent.tileId, active.id, intent.targetLevel);
          return;
        }

        // Vulture Raider — proactive set-collection deals: offer the missing colour
        // piece for a clear price (one Accept/Decline choice, no coefficients).
        if (archetype === 'OPPORTUNISTIC' && Math.random() < 0.35) {
          store.botProposeDistrictTrade();
          return;
        }

        // Autonomous B2B table and corporate-raider bids. Both are once-per-round
        // guarded in the store, so re-running this effect can never spam deals.
        const botDeal = store.executeBotTradingPhase(active.id);
        if (botDeal?.executed) return;
        store.aiInitiateHostileTakeover(active.id);
        if (useGameStore.getState().attention) return;

        // Passive float accumulation — the tranche comes from the archetype profile
        // (AGGRESSIVE max/51 %, OPPORTUNISTIC 26–35, CONSERVATIVE 25–40 within its floor).
        const stake = board
          .filter((tile) => tile.tileType === 'COMMERCIAL')
          .map((tile) => {
            const owned = tile.shares[active.id] ?? 0;
            const float = tile.totalShares - Object.values(tile.shares).reduce((sum, value) => sum + value, 0);
            return { tile, owned, float, weight: owned > 0 ? 2 : 1 };
          })
          .filter((entry) => entry.float >= 25)
          .sort((a, b) => b.weight * b.float - a.weight * a.float);
        if (stake.length > 0) {
          const drip = botEvaluatePurchase(st.board, active, stake[0].tile);
          if (drip.shares > 0) {
            const cost = store.botExecutePurchase(active.id, stake[0].tile.id, drip.shares, drip.structure, drip.note);
            store.setBotStatus({
              playerId: active.id,
              phase: 'GOVERN',
              text: `${t('ticker.botBought', { name: active.name, shares: drip.shares, tile: tileName(stake[0].tile.name, t), cost: money(cost) })}`,
            });
            return;
          }
        }
        store.finishTurn();
        return;
      }

      if (turnPhase === 'TILE_RESOLUTION' && !st.pendingOffer) {
        const occupied = st.players[st.currentPlayerIndex]?.id === active.id;
        if (occupied) st.setTurnPhase('TURN_END');
        return;
      }
      if (turnPhase === 'FAST_TRAVEL_SELECTION') {
        // Bots ride toward the richest station if flush, otherwise stay.
        const stations = st.board.map((tile, index) => ({ tile, index })).filter((s) => s.tile.tileType === 'TRANSIT' && s.index !== st.fastTravelFrom);
        if (stations.length > 0 && active.cash > 600) store.rideMetro(stations[Math.floor(Math.random() * stations.length)].index);
        else store.skipMetro();
        return;
      }
      if (turnPhase === 'CITY_HALL_LOBBY') {
        // Cartel lobbying: freeze the market leader's prime district or audit the hegemon.
        store.aiCityHallTactic(active.id);
        return;
      }
      if (turnPhase === 'TURN_END') store.finishTurn();
    }, botStepDelay);

    return () => clearTimeout(timer);
  }, [active, board, gameOver, pushLuckUsed, reachableIndices, store, turnPhase, pendingOffer, emergency, insolvency, attention, tradeOffer, speed, moneyTransfers.length, showIntro]);

  useEffect(() => {
    if (showIntro || !auction.isOpen) return;
    // Hard gate: a staged auction behind an unacknowledged attention intercept is never started.
    if (attention) return;
    // Hotseat turn-based bidding: the countdown only pressures the current BOT bidder.
    // Human bid slots pause the clock — a human at the controls is never auto-skipped.
    const currentBidder = players.find((p) => p.id === auction.turnOrder[auction.turnIndex]);
    if (!currentBidder || !currentBidder.isAI) return;
    const timer = setInterval(() => store.tickAuction(), 1000);
    return () => clearInterval(timer);
  }, [auction.isOpen, auction.turnIndex, auction.highestBidderId, auction.bidHistory, auction.timeRemaining, attention, store, players, showIntro]);

  useEffect(() => {
    if (showIntro || !gameStarted || gameOver || !active?.isAI) return;
    if (attention || tradeOffer || handover || hotseatReview || pendingOffer || auction.isOpen || insolvency) return; // legitimate human-facing gates — never watchdogged

    // Bot Watchdog: if the active bot's turn phase hasn't advanced for > 3.5s
    // (orphan gate, un-closed financial desk, missing handler), force-resolve all
    // pending tile settlement and advance the turn so the game can never freeze.
    const startedAt = Date.now();
    const watchdog = window.setInterval(() => {
      const st = useGameStore.getState();
      const current = st.players[st.currentPlayerIndex];
      if (!current || !current.isAI || st.gameOver) return;
      if (st.attention || st.tradeOffer || st.handover || st.hotseatReview || st.pendingOffer || st.auction.isOpen || st.insolvency) return;
      // Emergency modal raised by a bot debtor: auto-settle it instead of waiting.
      if (st.emergency) {
        st.autoResolveEmergency();
        return;
      }
      if (Date.now() - startedAt < 3500) return;
      st.botForceAdvance();
    }, 500);
    return () => window.clearInterval(watchdog);
  }, [gameStarted, gameOver, active, turnPhase, pendingOffer, emergency, insolvency, attention, tradeOffer, handover, hotseatReview, auction.isOpen, showIntro]);

  // Turn failsafe watchdog: if the ACTIVE player is a bot and the game makes no
  // progress for 4s (no attention modal, no open human auction), force-advance the
  // turn so the match can NEVER deadlock on a bot under any circumstances.
  useEffect(() => {
    if (showIntro || !gameStarted || gameOver || !active?.isAI) return;
    if (emergency || insolvency || attention || tradeOffer || handover || hotseatReview) return; // human gates — not a stall
    if (auction.isOpen) return; // auction loop has its own bot-bidder timer
    const timer = setTimeout(() => {
      const st = useGameStore.getState();
      const stillBot = st.players[st.currentPlayerIndex];
      if (!stillBot?.isAI || st.gameOver) return;
      if (st.attention || st.emergency || st.insolvency || st.tradeOffer || st.handover || st.hotseatReview) return;
      console.warn('[WATCHDOG] Bot stalled in phase:', st.turnPhase, 'Forcing next step...');
      st.botForceAdvance();
    }, 4000);
    return () => clearTimeout(timer);
    // Any state progression (phase, offer, auction tick) restarts the 4s window.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameStarted, gameOver, currentPlayerIndex, active?.isAI, turnPhase, pendingOffer, auction.isOpen, auction.turnIndex, auction.currentBid, attention, emergency, tradeOffer, handover, hotseatReview, showIntro]);

  const aliveBotIds = players.filter((p) => p.isAI && !p.isBankrupt).map((p) => p.id).join(',');
  const currentAuctionBidder = auction.isOpen ? players.find((p) => p.id === auction.turnOrder[auction.turnIndex]) : undefined;
  const botBidderId = currentAuctionBidder?.isAI ? currentAuctionBidder.id : null;
  // Turn-based bots: when it's a bot's bid slot, think briefly, then bid or fold.
  // NOTE: setThinking excluded from deps on purpose — each set() creates a new
  // auction object, scheduling a fresh timeout each render = infinite loop.
  useEffect(() => {
    if (showIntro || !auction.isOpen || !botBidderId || gameOver) return;
    if (auction.highestBidderId === botBidderId) { store.setThinking(null); return; }
    if (auction.foldedPlayerIds.includes(botBidderId)) return;
    if (auction.thinkingBidderId !== botBidderId) store.setThinking(botBidderId);
    const delay = speed === 'FAST' ? 250 + Math.random() * 200 : 600 + Math.random() * 300;
    const timer = setTimeout(() => {
      const st = useGameStore.getState();
      if (!st.auction.isOpen) return;
      // The slot may have advanced while the bot "thought" — verify it's still their call.
      const stillMine = st.auction.turnOrder[st.auction.turnIndex] === botBidderId;
      if (!stillMine) return;
      const me = st.players.find((p) => p.id === botBidderId);
      if (!me || me.isBankrupt || me.id === st.auction.highestBidderId) { st.setThinking(null); return; }
      const tile = st.board.find((t) => t.id === st.auction.propertyId);
      // Archetype compliance: one pure policy function decides bid / borrow-and-bid / fold
      // from ARCHETYPE_PROFILES (AGGRESSIVE 1.35× + credit leverage, CONSERVATIVE 0.85×,
      // OPPORTUNISTIC 1.05× neutral / 1.4× to block a monopoly).
      if (tile) {
        const verdict = botAuctionDecision({
          board: st.board,
          bot: me,
          tile,
          tranche: st.auction.shareTranche,
          currentBid: st.auction.currentBid,
          highestBidderId: st.auction.highestBidderId,
          creditAvailable: Math.max(0, st.debtCeilingOf(me.id) - me.debt),
        });
        if (verdict.action === 'BID') {
          st.placeBid(me.id, verdict.amount);
          return;
        }
        if (verdict.action === 'BORROW_AND_BID') {
          st.takeLoan(me.id, verdict.borrow);
          st.setBotStatus({
            playerId: me.id,
            phase: 'SETTLE',
            text: `${me.name} drew $${verdict.borrow} credit line to fund auction bid!`,
          });
          st.placeBid(me.id, verdict.amount);
          return;
        }
      }
      st.foldAuction(me.id);
    }, delay);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auction.isOpen, auction.turnIndex, auction.currentBid, auction.highestBidderId, auction.thinkingBidderId, botBidderId, aliveBotIds, speed, showIntro]);

  useEffect(() => {
    if (!gameOver) return;
    confetti({ particleCount: 180, spread: 90, origin: { y: 0.3 } });
  }, [gameOver]);

  // Cloud bootstrap: auth listener + debounced autosave (Firebase loaded lazily).
  useEffect(() => {
    void useCloudStore.getState().init();
  }, []);

  // The Stakko rail owns sign-in. When the player logs in there, the game adopts the
  // same Firebase account and loads its cloud saves without a second login prompt.
  useEffect(() => {
    const user = stakko?.user;
    void useCloudStore.getState().adoptUser(
      user
        ? {
            uid: user.uid,
            displayName: user.displayName ?? user.email?.split('@')[0] ?? 'Player',
            email: user.email ?? '',
            photoURL: user.photoURL ?? '',
            providerId: 'google.com',
          }
        : null,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stakkoUid]);

  // Landing screen: shown on boot and whenever the player opens the menu. A running
  // match can be resumed with one click; the setup lobby is right below it.
  if (showIntro || !gameStarted) {
    return <LobbyScreen onEnterGame={() => setShowIntro(false)} />;
  }

  return (
    <div className="flex h-dvh w-full flex-col overflow-hidden bg-[#121620] text-slate-100">
      <HandoverScreen />
      <HotseatTradeReview />
      <TradeModal open={tradeOpen} onClose={() => setTradeOpen(false)} />
      <TileGuideModal />
      <CloudSavePrompt />
      <header className="relative z-30 flex h-12 w-full flex-shrink-0 items-center justify-between gap-2 border-b border-slate-700/60 bg-[#171c28] px-3 shadow-sm">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setShowIntro(true)}
              className="flex items-center gap-2 rounded-xl border border-amber-300/30 bg-amber-400/10 px-2.5 py-1.5 text-sm font-black tracking-tight text-amber-100 transition hover:bg-amber-400/20"
              title={t('header.menu')}
            >
              <MenuIcon size={16} /> Frakto
            </button>
            <p className="hidden text-[11px] text-slate-400 md:block">{t('common.appTagline')}</p>
          </div>
          <div className="flex min-w-0 flex-wrap items-center gap-1.5 overflow-x-auto text-xs">
            <LangSwitcher />
            <button
              type="button"
              onClick={() => setSaveManagerOpen(true)}
              className="ml-2 flex items-center gap-1.5 rounded-lg bg-white/15 px-3 py-1.5 text-sm font-bold text-white transition hover:bg-white/25"
            >
              <SaveIcon size={14} /> {t('common.saveLoad')}
            </button>
            <button
              onClick={() => setHeatmap((value) => !value)}
              className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 font-bold transition ${heatmap ? 'bg-amber-500/30 text-amber-100' : 'bg-slate-800/80 text-slate-300 hover:bg-slate-700/80'}`}
            >
              <HeatmapIcon size={14} /> {t('header.heatmap')} {heatmap ? 'ON' : 'OFF'}
            </button>
            <button onClick={toggleSpeed} className="flex items-center gap-1.5 rounded-lg bg-slate-800/80 px-3 py-1.5 font-bold text-amber-200 transition hover:bg-slate-700/80">
              <SpeedIcon size={14} /> {t(speed === 'FAST' ? 'header.fast' : 'header.normal')}
            </button>
            <button
              onClick={() => { store.beginTradeNegotiation(); setTradeOpen(true); }}
              disabled={!isHumanTurn}
              className="flex items-center gap-1.5 rounded-lg bg-cyan-700/80 px-3 py-1.5 font-bold text-white transition hover:bg-cyan-600/80 disabled:bg-slate-700/70 disabled:text-slate-500"
            >
              <TradeIcon size={14} /> {t('header.trade')}
            </button>
            <select
              value={market.maxRounds}
              onChange={(e) => store.setGameLength(Number(e.target.value))}
              title={t('header.gameLength')}
              className="rounded-lg bg-slate-800/80 px-3 py-1.5 font-bold text-slate-200 transition hover:bg-slate-700/80"
            >
              <option value={0}>{t('header.endless')}</option>
              <option value={25}>{t('header.classic25')}</option>
            </select>
            <button
              onClick={() => { if (window.confirm(t('header.newGameConfirm'))) store.openLobby(); }}
              className="rounded-lg bg-slate-700/70 px-3 py-1.5 font-bold text-slate-200 transition hover:bg-slate-600/70"
              title={t('header.newGame')}
            >
              {t('header.newGame')}
            </button>
          </div>
        </header>

        {isMobile ? (
          <div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden">
            {/* Board fills the viewport — the fixed bottom nav reserves its own space.
                The board itself renders a fit-to-width SQUARE MINI-MAP (no panning):
                tap a cell to open the rich bottom-sheet detail card. */}
            <section className="flex min-h-0 w-full flex-1 flex-col overflow-hidden rounded-2xl bg-slate-950/60">
              <div className="min-h-0 w-full flex-1">
                <GameBoard key={epoch} heatmap={heatmap} />
              </div>
            </section>

            {/* Sticky Bottom Action Pill: the whole turn is playable with a thumb,
                pinned just above the mobile nav bar (safe-area aware). */}
            <div className="pointer-events-none fixed inset-x-0 bottom-16 z-30 flex w-full flex-col items-center gap-2 px-2 pb-safe">
              {active && !active.isAI && !gameOver ? (
                <div className="pointer-events-auto max-h-[38vh] w-full max-w-md overflow-y-auto rounded-2xl border border-slate-700/70 bg-slate-900/95 p-2 shadow-2xl backdrop-blur scrollbar-thin">
                  <ActionSteps />
                  <AuctionPanel />
                </div>
              ) : active && active.isAI && !gameOver ? (
                <div className="pointer-events-auto w-full max-w-md rounded-2xl border border-slate-700/70 bg-slate-900/95 px-3 py-2 shadow-2xl backdrop-blur">
                  <BotStatusBar />
                </div>
              ) : null}
            </div>
          </div>
        ) : (
          <main className="flex min-h-0 w-full flex-1 overflow-hidden p-1.5 sm:p-2">
            <section className="relative flex h-full min-h-0 min-w-0 flex-1 items-center justify-center overflow-hidden rounded-3xl">
              <GameBoard key={epoch} heatmap={heatmap} />
            </section>
          </main>
        )}

        {/* 3. Bottom Activity Bar (docked footer — never overlaps the board tiles) */}
        <footer
          className={`z-20 flex h-9 flex-shrink-0 items-center border-t border-slate-800 bg-slate-900/90 px-4 ${isMobile ? 'mb-16' : ''}`}
        >
          <ActivityTicker />
        </footer>

        {isMobile && (
          <MobileNav
            boardActive={!mobileSheet}
            onOpenFinance={() => store.openFinancialDesk('LOANS')}
            sheet={mobileSheet}
            onSheetChange={setMobileSheet}
            portfolioContent={renderSidebar()}
            actionDot={Boolean(
              active && human && active.id === human.id && !gameOver &&
              (turnPhase === 'TURN_START' || pendingOffer || auction.isOpen),
            )}
          />
        )}
        {/* Desk modal lives at the shell level so it opens from the board, the action
            card and the sidebar alike (never duplicated across tabs). */}
        <FinancialDeskModal />
        <InsolvencyModal />
        <NewPropertyAlertModal />
        {saveManagerOpen && <SaveManagerModal open={saveManagerOpen} onClose={() => setSaveManagerOpen(false)} />}
        <PlayerDossierModal />
    </div>
  );

  function renderSidebar() {
    return (
      <>
        <CompanySummary />
        <DistrictPortfolio />
        {renderGovernance()}
      </>
    );
  }

  function renderGovernance() {
    const voting = proposal.status === 'PENDING';
    if (!voting && turnPhase !== 'GOVERNANCE_PHASE') return null;
    const tile = board.find((entry) => entry.id === proposal.propertyId);
    return (
      <div className="glass rounded-2xl border border-amber-600/30 p-4 text-xs">
        <h2 className="mb-1 text-sm font-semibold uppercase tracking-widest text-amber-300">Corporate governance</h2>
        {tile ? (
          <>
            <p className="text-sm font-medium">
              {tile.name} → tier {proposal.targetUpgradeLevel}
            </p>
            <p className="text-slate-400">
              Capital call {money(proposal.totalCost)} · +{proposal.newSharesIssued} shares issued (pre-emptive rights apply)
            </p>
            <div className="mt-2 flex h-2 overflow-hidden rounded-full bg-slate-700/70">
              <span style={{ width: `${Math.min(100, (proposal.votesFor / Math.max(1, tile.totalShares)) * 100)}%` }} className="bg-emerald-500" />
              <span style={{ width: `${Math.min(100, (proposal.votesAgainst / Math.max(1, tile.totalShares)) * 100)}%` }} className="bg-rose-500" />
            </div>
            <p className="mt-1 text-slate-400">
              {proposal.votesFor} for / {proposal.votesAgainst} against · of {tile.totalShares} shares issued
            </p>
            <div className="mt-2 grid gap-2">
              <button
                disabled={!human || proposal.votedPlayerIds.includes(human.id)}
                onClick={() => human && store.voteOnProposal(human.id, 'FOR')}
                className="min-h-[56px] w-full rounded-2xl border-2 border-emerald-400/60 bg-gradient-to-r from-emerald-600/90 to-teal-600/90 px-5 py-3 text-base font-extrabold text-white transition hover:from-emerald-500 hover:to-teal-500 disabled:cursor-not-allowed disabled:border-slate-700 disabled:from-slate-800 disabled:to-slate-800 disabled:text-slate-500"
              >
                Approve &amp; invest
              </button>
              <button
                disabled={!human || proposal.votedPlayerIds.includes(human.id)}
                onClick={() => human && store.voteOnProposal(human.id, 'AGAINST')}
                className="min-h-[56px] w-full rounded-2xl border-2 border-rose-400/60 bg-gradient-to-r from-rose-600/90 to-red-600/90 px-5 py-3 text-base font-extrabold text-white transition hover:from-rose-500 hover:to-red-500 disabled:cursor-not-allowed disabled:border-slate-700 disabled:from-slate-800 disabled:to-slate-800 disabled:text-slate-500"
              >
                Reject proposal
              </button>
            </div>
            <button
              disabled={!human || human.cash < Math.round(proposal.totalCost * (ownershipPct(tile, human.id) / 100))}
              onClick={() =>
                human &&
                store.executeUpgrade(
                  tile.id,
                  proposal.targetUpgradeLevel,
                  proposal.totalCost,
                  proposal.newSharesIssued,
                )
              }
              className="mt-2 min-h-[56px] w-full rounded-2xl border-2 border-violet-400/60 bg-gradient-to-r from-violet-600/90 to-fuchsia-600/90 px-5 py-3 text-base font-extrabold text-white transition hover:from-violet-500 hover:to-fuchsia-500 disabled:cursor-not-allowed disabled:border-slate-700 disabled:from-slate-800 disabled:to-slate-800 disabled:text-slate-500"
            >
              Force capital call (dilute non-paying rivals)
            </button>
          </>
        ) : (
          <p className="text-slate-400">
            Financial desk open — structure a loan, park idle cash in a fund, table an upgrade from your cap table, or walk away.
          </p>
        )}
        <div className="mt-2 grid gap-2">
          <button
            onClick={() => store.openFinancialDesk('LOANS')}
            className="w-full min-h-[48px] rounded-xl border-2 border-amber-500/40 bg-gradient-to-r from-amber-600/30 to-amber-700/20 px-4 py-2.5 text-sm font-bold text-amber-200 shadow-lg hover:border-amber-400"
          >
            Otevřít finanční desku (Úvěry, Spoření, Pojištění)
          </button>
          <button
            disabled={!isHumanTurn}
            onClick={() => store.finishTurn()}
            className="min-h-[56px] w-full rounded-2xl border-2 border-emerald-300 bg-gradient-to-r from-emerald-400 to-teal-300 px-5 py-3 text-base font-black text-slate-950 transition hover:from-emerald-300 hover:to-teal-200 disabled:border-slate-700 disabled:from-slate-800 disabled:to-slate-800 disabled:text-slate-500"
          >
            Ukončit tah
          </button>
        </div>

        {human && (() => {
          const candidates = board
            .filter((tile) => (tile.shares[human.id] ?? 0) > 0 && players.some((p) => p.id !== human.id && (tile.shares[p.id] ?? 0) > 0))
            .slice(0, 4);
          if (candidates.length === 0) return null;
          return (
            <div className="mt-2 grid gap-1">
              <p className="text-[10px] font-bold uppercase tracking-widest text-rose-300">Hostile takeover targets (150% premium, 1×/round)</p>
              {candidates.map((tile) => {
                const unit = (tile.valuation > 0 ? tile.valuation : tile.baseCost) / tile.totalShares;
                const cost = players.reduce((s, p) => (p.id !== human.id ? s + Math.ceil((tile.shares[p.id] ?? 0) * unit * 1.5) : s), 0);
                return (
                  <button
                    key={tile.id}
                    disabled={!isHumanTurn || human.cash < cost}
                    onClick={() => human && store.startTakeover(tile.id)}
                    className="min-h-[56px] w-full rounded-2xl border-2 border-rose-500/70 bg-gradient-to-r from-rose-600/90 to-orange-600/90 px-5 py-3 text-left text-base font-extrabold text-white hover:from-rose-500 hover:to-orange-500 disabled:opacity-40"
                  >
                    Takeover {tile.name} · {money(cost)}
                  </button>
                );
              })}
            </div>
          );
        })()}
      </div>
    );
  }
};

export default App;
